import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { useEffect, useState } from "react";

import FileDropZone from "@/components/common/FileDropZone";
import { errorMessage } from "@/lib/api";
import { importPurchaseOrder } from "./api";

/**
 * 発注書 PDF を取り込むダイアログ（明細タブから開く）。
 *
 * **上げ直せる。** 客先が内容を変えて再発行することがあるので、2 通目以降は
 * **いまある明細に無い行だけが足される**（図番で突き合わせる）。既にある行は
 * 書き換えない —— 人が手で直しているかもしれず、紙で上書きするとその修正が
 * 黙って消えるため。数量や単価が食い違う行は足しも直しもせず、明細タブの帯に
 * 図番が出る。
 *
 * 読み取りは別プロセスのワーカーがやるので（[[purchase_orders]]）、ここは
 * 送るだけで閉じる。結果は明細タブに出てくる。
 */
export default function PurchaseOrderUploadDialog({
  projectId,
  isOpen,
  onClose,
  onUploaded,
}: {
  projectId: number;
  isOpen: boolean;
  onClose: () => void;
  onUploaded: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setFile(null);
    setError(null);
    setSaving(false);
  }, [isOpen]);

  const submit = async () => {
    if (!file) return;
    setSaving(true);
    setError(null);
    try {
      await importPurchaseOrder(projectId, file);
      onUploaded();
      onClose();
    } catch (e) {
      setError(errorMessage(e, "発注書を取り込めませんでした。"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={(open) => !open && onClose()} size="lg">
      <ModalContent>
        <ModalHeader className="flex-col items-start gap-1">
          <span>発注書の取り込み</span>
          <span className="text-small font-normal text-default-500">
            図番・数量・単価を読み取って明細に追加します。
            <strong>既にある図番の行は追加しません。</strong>
          </span>
        </ModalHeader>

        <ModalBody>
          <FileDropZone
            label="発注書 PDF"
            accept=".pdf"
            hint="読み取りには数秒かかります"
            file={file}
            isDisabled={saving}
            onSelect={(selected) => {
              setFile(selected);
              setError(null);
            }}
          />
          {error && <p className="text-sm text-danger">{error}</p>}
        </ModalBody>

        <ModalFooter>
          <Button variant="light" radius="full" data-testid="cancel" onPress={onClose}>
            キャンセル
          </Button>
          <Button
            color="primary"
            radius="full"
            data-testid="purchase-order-submit"
            isDisabled={file == null}
            isLoading={saving}
            onPress={() => void submit()}
          >
            取り込む
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
